import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";
import api from "../../utils/api";
import { normalizeBillingSidebarData } from "../../utils/billingSidebar";

const initialState = {
    isLoading: false,
    error: null,
    billingList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    billingSidebarData: null,
    selectedEntity: null,
    purchaseHistoryList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    sendPayBalanceOtpData : null,
    invoiceDetails: {},
    entityDetails: {},
    entityInvoiceList: { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 },
    postpaidBilling: [],
    postpaidBillingDetails: {}
}

export const fetchAllEntityBillingList = createAsyncThunk("viewClientBilling/fetchAllEntityBillingList", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchAllEntityBillingList + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchBillingSidebarData = createAsyncThunk("viewClientBilling/fetchBillingSidebarData", async (queryParams = "") => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.billingSidebarData + queryParams
        };
        const response = await api(data);
        return normalizeBillingSidebarData(response.data);

    } catch (error) {
        throw error?.response?.data;
    }
});

export const sendPayBalanceOtp = createAsyncThunk("viewClientBilling/sendPayBalanceOtp", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.sendPayBalanceOtp,
            data: payload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const verifyPayBalanceOtp = createAsyncThunk("viewClientBilling/verifyPayBalanceOtp", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.verifyPayBalanceOtp,
            data: payload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchInvoiceDetails = createAsyncThunk("viewClientBilling/fetchInvoiceDetail", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchInvoiceDetail + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});
export const getEntityDetails = createAsyncThunk("entity/getEntityDetails", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getEntityDetails + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const entityPreviousInvoiceList = createAsyncThunk("invoice/entityPreviousInvoiceList", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.entityPreviousInvoiceList + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchPostpaidBillingList = createAsyncThunk("invoice/fetchPostpaidBillingList", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchPostpaidBillingList + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchPostpaidClientBillingDetails = createAsyncThunk("invoice/fetchPostpaidClientBillingDetails", async (queryParams) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.fetchPostpaidClientBillingDetails + queryParams
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

export const generatePostpaidInvoice = createAsyncThunk("invoice/generatePostpaidInvoice", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.generatePostpaidInvoice,
            data: payload
        };
        const response = await api(data);
        return response.data;

    } catch (error) {
        throw error?.response?.data;
    }
});

const viewClientBillingSlice = createSlice({
    name: "viewClientBilling",
    initialState,
    reducers: {
        resetData(state, action) {
            state.invoiceDetails = {}
            state.selectedEntity = null;
            state.purchaseHistoryList = { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllEntityBillingList.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.billingList = payload.data;
            })
            .addCase(fetchBillingSidebarData.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.billingSidebarData = payload?.raw ?? null;
                state.billingList = payload?.entities ?? { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 };
                state.selectedEntity = payload?.selectedEntity ?? null;
                state.purchaseHistoryList = payload?.purchaseHistory ?? { rows: [], count: 0, totalPages: 0, currentPage: 1, type: 200 };
                state.entityInvoiceList = payload?.selectedEntity?.invoices ?? state.entityInvoiceList;
                state.entityDetails = payload?.selectedEntity ?? state.entityDetails;
            })
            .addCase(sendPayBalanceOtp.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.sendPayBalanceOtpData = payload.data;
            })
            .addCase(verifyPayBalanceOtp.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(fetchInvoiceDetails.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.invoiceDetails = payload?.data;
            })
            .addCase(entityPreviousInvoiceList.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.entityInvoiceList = payload?.data;
            })
            .addCase(getEntityDetails.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.entityDetails = payload?.data;
            })
            .addCase(fetchPostpaidBillingList.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.postpaidBilling = payload?.data;
            })
            .addCase(fetchPostpaidClientBillingDetails.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.postpaidBillingDetails = payload?.data;
            })
            .addCase(generatePostpaidInvoice.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
            })
            .addMatcher(isAnyOf(fetchAllEntityBillingList.pending, fetchBillingSidebarData.pending, fetchInvoiceDetails.pending, sendPayBalanceOtp.pending, verifyPayBalanceOtp.pending,entityPreviousInvoiceList.pending,getEntityDetails.pending, fetchPostpaidBillingList.pending, fetchPostpaidClientBillingDetails.pending, generatePostpaidInvoice.pending
            ), (state) => {
                state.isLoading = true;
            })
            .addMatcher(isAnyOf(fetchAllEntityBillingList.rejected, fetchBillingSidebarData.rejected, sendPayBalanceOtp.rejected, verifyPayBalanceOtp.rejected, fetchInvoiceDetails.rejected,entityPreviousInvoiceList.rejected, getEntityDetails.rejected, fetchPostpaidBillingList.rejected, fetchPostpaidClientBillingDetails.rejected, generatePostpaidInvoice.rejected), (state, { error }) => {
                state.isLoading = false;
                state.error = error.message ? error.message : "Request Failed Please Try Again ";
            })
    }
});
const { resetData } = viewClientBillingSlice.actions;
const viewClientBillingReducer = viewClientBillingSlice.reducer;
export { resetData, viewClientBillingReducer };
export default viewClientBillingReducer;
